/** What `CodeBlockPaginator` deals out, and the box it deals them out of — see descriptionBlocks.ts. */
const CODE_WRAPPER_CLASS = "child-wrapper";
const CODE_LINE_CLASS = "code-line";

/** The viewer's own code panes: request and response examples, code samples. */
const VIEWER_CODE_SELECTOR = "openapi-doc pre.code-block";

/**
 * An empty line still takes a line on paper. The paginator books a line by the height of its element, and an
 * empty inline box has none — a zero-width space gives it the height of the text around it.
 */
const EMPTY_LINE = "​";

const newLine = (): HTMLElement => {
	const line = document.createElement("span");
	line.className = CODE_LINE_CLASS;
	return line;
};

/**
 * Gives the viewer's code panes the lines `CodeBlockPaginator` deals out, keeping the highlighting.
 *
 * The viewer prints every example and every code sample in full, and a long one is taller than a page. Split
 * nowhere, it went to paper as one block and lost everything past the edge of the sheet. The paginator already
 * splits the article's code blocks line by line — this hands it the same markup. Unlike a description's code
 * block (descriptionBlocks.ts), these panes are highlighted, so a token that spans a line break is cut in two
 * and each half keeps the token's classes.
 */
export const lineViewerCodeBlocks = (block: HTMLElement): void => {
	for (const pre of block.querySelectorAll<HTMLPreElement>(VIEWER_CODE_SELECTOR)) {
		if (pre.querySelector(`.${CODE_WRAPPER_CLASS}`)) continue;

		const lines: HTMLElement[] = [];
		let line = newLine();
		const place = (text: string, tokens: Element[]) => {
			let piece: Node = document.createTextNode(text);
			for (const token of [...tokens].reverse()) {
				const shell = token.cloneNode(false);
				shell.appendChild(piece);
				piece = shell;
			}
			line.appendChild(piece);
		};
		const walk = (node: Node, tokens: Element[]) => {
			if (node.nodeType === Node.TEXT_NODE) {
				(node.textContent ?? "").split("\n").forEach((part, index) => {
					if (index) {
						lines.push(line);
						line = newLine();
					}
					if (part) place(part, tokens);
				});
				return;
			}
			if (node.nodeType !== Node.ELEMENT_NODE) return;
			for (const child of [...node.childNodes]) walk(child, [...tokens, node as Element]);
		};
		for (const child of [...pre.childNodes]) walk(child, []);
		lines.push(line);

		// A trailing line break ends the last line; it is not a line of its own.
		while (lines.length > 1 && !lines[lines.length - 1].textContent) lines.pop();

		const wrapper = document.createElement("div");
		wrapper.className = CODE_WRAPPER_CLASS;
		lines.forEach((item, index) => {
			if (!item.textContent) item.textContent = EMPTY_LINE;
			// The paginator puts the same separator between the lines it deals out, so a page break lands where
			// a line break was.
			if (index) wrapper.appendChild(document.createTextNode("\n"));
			wrapper.appendChild(item);
		});
		pre.replaceChildren(wrapper);
	}
};

export default lineViewerCodeBlocks;
